/* Forte PDP — Judge.me hydration, photo slider and review pagination.

   Judge.me exposes this product's reviews as a pre-rendered HTML blob
   (product.metafields.judgeme.widget), so Liquid cannot read them as fields.
   The section drops that blob into an inert <template>; this reads the data
   attributes off Judge.me's own markup and rebuilds the histogram, the review
   list and the photo strip using the Forte card design.

   The blob only ever carries the first 4 reviews, so LOAD MORE pages
   api.judge.me/reviews/reviews_for_widget, which is public (no token) and
   returns the same markup shape.

   Everything here is progressive enhancement. The score and count are already
   server-rendered from Shopify's `reviews` metafields, and with JS off (or no
   Judge.me data) the section keeps the section blocks, i.e. the static comp. */
(function () {
  'use strict';

  var root = document.querySelector('[data-fpdp-rv]');
  if (!root) return;
  var tpl = root.querySelector('template[data-fpdp-jm]');
  if (!tpl) return;

  var doc = tpl.content;
  var assets = root.dataset.assets ? JSON.parse(root.dataset.assets) : {};
  var STAR = assets.star || '';
  var STAR_SM = assets.star_sm || '';
  var VERIFIED = assets.verified || '';

  var SHOP = root.dataset.jmShop || '';
  var PRODUCT = root.dataset.jmProduct || '';
  var TOTAL = parseInt(root.dataset.jmTotal, 10) || 0;
  var PAGE_SIZE = parseInt(root.dataset.jmPageSize, 10) || 10;
  var API = 'https://api.judge.me/reviews/reviews_for_widget';

  /* Figma #8174:5109 — 150px tile + 12px gap. */
  var TILE = 162;

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function txt(scope, sel) {
    var n = scope.querySelector(sel);
    return n ? n.textContent.trim() : '';
  }

  function img(src, w, h, cls) {
    var n = document.createElement('img');
    n.src = src;
    n.alt = '';
    if (w) n.width = w;
    if (h) n.height = h;
    if (cls) n.className = cls;
    return n;
  }

  /* Judge.me serves review images through a resizing proxy, so ask it for the
     size the slot actually renders at instead of scaling a 1024px original. */
  function sized(url, w) {
    if (!url) return '';
    return /([?&])width=\d+/.test(url)
      ? url.replace(/([?&])width=\d+/, '$1width=' + w)
      : url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  /* The comp draws a full five-star track on every review. Judge.me scores are
     whole numbers, so fill that many and leave the rest tinted (.is-off). */
  function stars(score) {
    var on = Math.round(score || 0);
    var wrap = el('span', 'fpdp-rv__stars');
    for (var i = 1; i <= 5; i++) {
      wrap.appendChild(img(STAR, 20, 20, i > on ? 'is-off' : null));
    }
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', on + ' out of 5 stars');
    return wrap;
  }

  /* ---------- AI review summary -> "Customers say" ----------
     Judge.me ships this inside the same widget blob once the shop enables
     "AI review summary". The feature is off on this shop today, so the exact
     class could not be observed — several of their known containers are tried
     and the first with real prose wins. The block ships hidden (.is-pending)
     and is only revealed on a match, so nothing empty or fabricated is ever
     shown; if a merchant types their own summary the section renders it
     server-side and this leaves it alone. */
  var sayBlock = root.querySelector('[data-jm-say]');
  var sayBody = root.querySelector('[data-jm-say-body]');

  if (sayBlock && sayBody && sayBlock.classList.contains('is-pending')) {
    var SAY_SELECTORS = [
      '.jdgm-ai-summary__content',
      '.jdgm-ai-summary__text',
      '.jdgm-ai-summary',
      '.jdgm-review-summary__content',
      '.jdgm-review-summary',
      '[class*="ai-summary"]',
      '[data-ai-summary]'
    ];

    var summary = '';
    for (var si = 0; si < SAY_SELECTORS.length && !summary; si++) {
      var hit = doc.querySelector(SAY_SELECTORS[si]);
      if (!hit) continue;
      var txt = (hit.textContent || '').replace(/\s+/g, ' ').trim();
      // Guard against picking up the feature's title or disclaimer rather than
      // the summary itself — a real one is a sentence, not a label.
      if (txt.length > 60) summary = txt;
    }

    if (summary) {
      sayBody.textContent = summary;
      sayBlock.classList.remove('is-pending');
    }
  }

  /* ---------- histogram -> the 5/4/3/2/1 bars ---------- */
  var bars = root.querySelector('[data-jm-bars]');
  // Judge.me emits a sixth row with data-rating="null" for reviews left with no
  // star rating. It is not one of the 5/4/3/2/1 bars, so drop it and keep the
  // rest ordered high to low regardless of the order Judge.me emitted them in.
  var rows = Array.prototype.filter.call(
    doc.querySelectorAll('.jdgm-histogram__row[data-rating]'),
    function (r) {
      var n = parseInt(r.getAttribute('data-rating'), 10);
      return n >= 1 && n <= 5;
    }
  ).sort(function (a, b) {
    return parseInt(b.getAttribute('data-rating'), 10) - parseInt(a.getAttribute('data-rating'), 10);
  });

  if (bars && rows.length) {
    bars.textContent = '';
    rows.forEach(function (r) {
      var rating = r.getAttribute('data-rating');
      var freq = r.getAttribute('data-frequency') || '0';
      var pct = parseFloat(r.getAttribute('data-percentage')) || 0;

      var bar = el('div', 'fpdp-rv__bar');
      var left = el('div', 'fpdp-rv__bar-left');
      var label = el('span', 'fpdp-rv__bar-label');
      label.appendChild(el('span', null, rating));
      if (STAR_SM) label.appendChild(img(STAR_SM, 14, 14));
      var trackEl = el('span', 'fpdp-rv__bar-track');
      if (pct > 0) {
        var fill = el('span', 'fpdp-rv__bar-fill');
        fill.style.width = pct + '%';
        trackEl.appendChild(fill);
      }
      left.appendChild(label);
      left.appendChild(trackEl);
      bar.appendChild(left);
      bar.appendChild(el('span', 'fpdp-rv__bar-count', freq));
      bars.appendChild(bar);
    });
  }

  /* ---------- reviews -> the card list ---------- */

  // Judge.me stamps "2026-07-17 02:19:41 UTC"; Safari will not parse that. Read
  // the UTC parts so the date cannot slide a day either side of the dateline.
  function formatDate(raw) {
    if (!raw) return '';
    var d = new Date(raw.replace(' UTC', 'Z').replace(' ', 'T'));
    if (isNaN(d.getTime())) return '';
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    return pad(d.getUTCMonth() + 1) + '/' + pad(d.getUTCDate()) + '/' + d.getUTCFullYear();
  }

  function buildCard(rev) {
    var ratingEl = rev.querySelector('.jdgm-rev__rating');
    var score = ratingEl ? parseFloat(ratingEl.getAttribute('data-score')) || 0 : 0;
    var author = txt(rev, '.jdgm-rev__author');
    var title = txt(rev, '.jdgm-rev__title');
    var body = txt(rev, '.jdgm-rev__body');
    var stampEl = rev.querySelector('.jdgm-rev__timestamp');
    var date = formatDate(stampEl && stampEl.getAttribute('data-content'));
    var verified = rev.getAttribute('data-verified-buyer') === 'true';

    var art = el('article', 'fpdp-rv__item');

    var who = el('div', 'fpdp-rv__who');
    // Judge.me supplies an initial, not a portrait. Showing a stock face next
    // to a real customer's name would misrepresent them, so use the initial.
    var initial = (txt(rev, '.jdgm-rev__icon') || author || '?').charAt(0).toUpperCase();
    var av = el('span', 'fpdp-rv__avatar fpdp-rv__avatar--initial', initial);
    av.setAttribute('aria-hidden', 'true');
    who.appendChild(av);

    var whotext = el('div', 'fpdp-rv__whotext');
    var nameline = el('span', 'fpdp-rv__nameline');
    nameline.appendChild(el('span', 'fpdp-rv__name', author));
    if (verified && VERIFIED) {
      var vi = img(VERIFIED, 16, 16);
      vi.alt = 'Verified';
      nameline.appendChild(vi);
    }
    whotext.appendChild(nameline);
    if (verified) whotext.appendChild(el('span', 'fpdp-rv__buyer', 'Verified Buyer'));
    if (date) whotext.appendChild(el('span', 'fpdp-rv__date', date));
    who.appendChild(whotext);
    art.appendChild(who);

    var bodyWrap = el('div', 'fpdp-rv__body');
    bodyWrap.appendChild(stars(score));
    var inner = el('div', 'fpdp-rv__bodyinner');
    var copy = el('div', 'fpdp-rv__copy');
    // Only the review's own title goes here. Judge.me's custom-question answers
    // ("Hair Type: Straight") are available on .jdgm-rev__cf-ans but are left
    // out by design — 30 of this product's 70 reviews have no title at all, so
    // the slot simply stays empty rather than filling with a different fact.
    if (title) copy.appendChild(el('h3', 'fpdp-rv__item-title', title));
    if (body) copy.appendChild(el('p', 'fpdp-rv__text fpdp-rv__text--large', body));
    inner.appendChild(copy);

    var picLink = rev.querySelector('.jdgm-rev__pic-link');
    var picImg = rev.querySelector('.jdgm-rev__pic-img');
    var full = (picLink && (picLink.getAttribute('href') || picLink.getAttribute('data-mfp-src'))) ||
               (picImg && (picImg.getAttribute('data-src') || picImg.getAttribute('src')));
    if (full) {
      var media = el('div', 'fpdp-rv__media');
      var mi = img(sized(full, 506));
      mi.loading = 'lazy';
      media.appendChild(mi);
      inner.appendChild(media);
    }
    bodyWrap.appendChild(inner);
    art.appendChild(bodyWrap);
    return art;
  }

  var list = root.querySelector('[data-jm-list]');
  var seenReviews = Object.create(null);
  var shown = 0;

  function addReviews(scope) {
    if (!list) return 0;
    var added = 0;
    Array.prototype.forEach.call(scope.querySelectorAll('.jdgm-rev'), function (r) {
      var id = r.getAttribute('data-review-id') || '';
      if (id && seenReviews[id]) return;
      if (id) seenReviews[id] = true;
      list.appendChild(buildCard(r));
      added++;
      shown++;
    });
    return added;
  }

  if (list && doc.querySelector('.jdgm-rev')) {
    list.textContent = '';
    addReviews(doc);
  }

  /* ---------- customer photos ---------- */
  var track = root.querySelector('[data-jm-strip-track]');
  var viewport = root.querySelector('[data-jm-strip-viewport]');
  var nav = root.querySelector('[data-jm-strip-nav]');
  var prevBtn = root.querySelector('[data-jm-strip-prev]');
  var nextBtn = root.querySelector('[data-jm-strip-next]');
  var photosWrap = root.querySelector('[data-jm-photos]');
  var seenPhotos = Object.create(null);

  // The same photo reaches us from two places under two different hosts —
  // the gallery JSON uses review-images.judge.me and the review cards use
  // review-images.judgeme.com — so key on the path alone, minus the resize
  // query, or every photo lands in the strip twice.
  function photoKey(url) {
    return String(url).split('?')[0].replace(/^https?:\/\/[^/]+/i, '');
  }

  function addPhotos(items) {
    if (!track) return;
    items.forEach(function (it) {
      if (!it.url) return;
      var key = photoKey(it.url);
      if (seenPhotos[key]) return;
      seenPhotos[key] = true;
      var span = el('span', 'fpdp-rv__photo');
      var i = img(sized(it.url, 300));
      i.loading = 'lazy';
      if (it.alt) i.alt = it.alt; else i.setAttribute('role', 'presentation');
      span.appendChild(i);
      // Opens in the lightbox; a bigger crop than the 150px tile is showing.
      span.dataset.full = sized(it.url, 1600);
      span.setAttribute('role', 'button');
      span.setAttribute('tabindex', '0');
      span.setAttribute('aria-label', it.alt ? 'Enlarge ' + it.alt : 'Enlarge customer photo');
      track.appendChild(span);
    });
    syncNav();
  }

  /* ---------- photo lightbox ---------- */
  var lightbox = null;
  var lightboxImg = null;
  var lastFocus = null;

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.dataset.open = 'false';
    lightboxImg.removeAttribute('src');
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  function buildLightbox() {
    lightbox = el('div', 'fpdp-rv__lightbox');
    lightbox.dataset.open = 'false';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'Customer photo');

    var close = el('button', 'fpdp-rv__lightbox-close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close photo');
    close.addEventListener('click', closeLightbox);

    lightboxImg = document.createElement('img');
    lightboxImg.alt = '';

    lightbox.appendChild(lightboxImg);
    lightbox.appendChild(close);

    // Clicking the scrim closes; clicking the photo itself does not.
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });

    root.appendChild(lightbox);
    return close;
  }

  function openLightbox(tile) {
    var full = tile.getAttribute('data-full') ||
               (tile.querySelector('img') && tile.querySelector('img').src);
    if (!full) return;
    var close = lightbox ? lightbox.querySelector('.fpdp-rv__lightbox-close') : buildLightbox();
    lastFocus = document.activeElement;
    lightboxImg.src = full;
    lightboxImg.alt = tile.getAttribute('aria-label') || '';
    lightbox.dataset.open = 'true';
    // Stops the page scrolling behind the overlay.
    document.documentElement.style.overflow = 'hidden';
    if (close && close.focus) close.focus();
  }

  // Delegated, so tiles added by a later page of reviews work without rebinding.
  root.addEventListener('click', function (e) {
    var tile = e.target.closest ? e.target.closest('.fpdp-rv__photo') : null;
    if (tile && root.contains(tile)) openLightbox(tile);
  });

  root.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var tile = e.target.closest ? e.target.closest('.fpdp-rv__photo') : null;
    if (!tile) return;
    e.preventDefault();
    openLightbox(tile);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && lightbox && lightbox.dataset.open === 'true') closeLightbox();
  });

  // The `jdgm-gallery-data` island is real JSON and lists every review that has
  // media, independent of which review page it falls on — so it, not the first
  // page of cards, is the source for the strip.
  function galleryPhotos() {
    var node = doc.querySelector('.jdgm-gallery-data[data-json]');
    if (!node) return [];
    var items;
    try { items = JSON.parse(node.getAttribute('data-json')); } catch (e) { return []; }
    if (!Array.isArray(items)) return [];
    var out = [];
    items.forEach(function (it) {
      (it.pictures_urls || []).forEach(function (p) {
        var url = (p && (p.original || p.compact)) || '';
        if (url) out.push({ url: url, alt: it.reviewer_name ? it.reviewer_name + ' review photo' : '' });
      });
    });
    return out;
  }

  // Any review page we load can carry photos the gallery island did not list,
  // so harvest those too. addPhotos() de-dupes, so the overlap is free.
  function photosFrom(scope) {
    var out = [];
    Array.prototype.forEach.call(scope.querySelectorAll('.jdgm-rev__pic-link'), function (a) {
      var url = a.getAttribute('href') || a.getAttribute('data-mfp-src');
      if (url) out.push({ url: url, alt: a.getAttribute('aria-label') || '' });
    });
    return out;
  }

  if (track) {
    var seeded = galleryPhotos().concat(photosFrom(doc));
    if (seeded.length) {
      // Clear the design's placeholder tiles only once there are real photos to
      // put in their place — a strip of stock images beside real reviews would
      // read as customer photos without being any.
      track.textContent = '';
      addPhotos(seeded);
    } else if (photosWrap) {
      photosWrap.hidden = true;
    }
  }

  /* ---------- photo slider — Figma #8174:5128 ---------- */
  function step() {
    if (!viewport) return TILE;
    return Math.max(TILE, Math.floor(viewport.clientWidth / TILE) * TILE);
  }

  function syncNav() {
    if (!viewport || !nav) return;
    var over = viewport.scrollWidth - viewport.clientWidth;
    if (over <= 1) {
      // Nothing to slide — two dead arrows would only mislead.
      nav.hidden = true;
      return;
    }
    nav.hidden = false;
    var x = viewport.scrollLeft;
    if (prevBtn) prevBtn.disabled = x <= 1;
    if (nextBtn) nextBtn.disabled = x >= over - 1;
  }

  // Plain assignment, deliberately: __strip-viewport carries
  // `scroll-behavior: smooth`, so the browser animates this and honours
  // prefers-reduced-motion for us. No scrollTo() options-object to feature-test.
  function slide(dir) {
    if (!viewport) return;
    var max = viewport.scrollWidth - viewport.clientWidth;
    var left = viewport.scrollLeft + dir * step();
    viewport.scrollLeft = Math.max(0, Math.min(left, max));
  }

  if (viewport && nav) {
    if (prevBtn) prevBtn.addEventListener('click', function () { slide(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { slide(1); });
    viewport.addEventListener('scroll', syncNav);
    window.addEventListener('resize', syncNav);
    syncNav();
  }

  /* ---------- load more -> page the public Judge.me API ---------- */
  var more = root.querySelector('[data-jm-more]');
  var moreLabel = root.querySelector('[data-jm-more-label]');
  var nextPage = 1;
  var busy = false;

  function syncMore() {
    if (!more) return;
    // TOTAL is the count Judge.me synced into the `reviews` metafield, so it is
    // the honest stopping point even before a page comes back empty.
    more.hidden = !shown || (TOTAL > 0 && shown >= TOTAL);
  }

  function loadMore() {
    if (busy || !list) return;
    busy = true;
    if (more) more.setAttribute('aria-busy', 'true');
    var original = moreLabel ? moreLabel.textContent : '';
    if (moreLabel) moreLabel.textContent = 'LOADING…';

    var url = API +
      '?url=' + encodeURIComponent(SHOP) +
      '&shop_domain=' + encodeURIComponent(SHOP) +
      '&platform=shopify' +
      '&product_id=' + encodeURIComponent(PRODUCT) +
      '&page=' + nextPage +
      '&per_page=' + PAGE_SIZE;

    fetch(url, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('Judge.me responded ' + r.status);
        return r.json();
      })
      .then(function (data) {
        // Page 1 overlaps the reviews already rendered from the blob; addReviews
        // de-dupes on data-review-id, so the first click tops up the rest of
        // that page and later pages come through whole.
        var holder = document.createElement('template');
        holder.innerHTML = data && data.html ? data.html : '';
        var added = addReviews(holder.content);
        addPhotos(photosFrom(holder.content));
        if (data && data.total_count) TOTAL = parseInt(data.total_count, 10) || TOTAL;
        nextPage++;
        if (moreLabel) moreLabel.textContent = original;
        if (!added) {
          // Judge.me has nothing further for us, whatever the count claimed.
          if (more) more.hidden = true;
        } else {
          syncMore();
        }
      })
      .catch(function () {
        // Leave the button in place so the reader can retry; the reviews
        // already on the page are unaffected.
        if (moreLabel) moreLabel.textContent = 'TRY AGAIN';
      })
      .then(function () {
        busy = false;
        if (more) more.removeAttribute('aria-busy');
      });
  }

  if (more) {
    if (SHOP && PRODUCT) {
      more.addEventListener('click', loadMore);
      syncMore();
    } else {
      more.hidden = true;
    }
  }

  // The cached widget metafield can carry the score and histogram but no
  // individual reviews (older Judge.me snapshot). Load page 1 from the live
  // Judge.me API so the list is never empty on a product that has reviews.
  if (list && !shown && SHOP && PRODUCT) {
    list.textContent = '';
    loadMore();
  }

  // Done with the blob.
  tpl.remove();
})();
